import React, { useState } from 'react';
import { Alert, Form, ControlLabel, Row, Col } from 'react-bootstrap';
import { Formik, FormikHelpers as FormikActions, FieldArray } from 'formik';
import { PrimaryButton, SecondaryButton } from '@magoosh/lib/button';
import style from './style.module.scss';
import cx from 'classnames';
import { RecommendationEditorProps, RecommendationEditorFormValues } from './types';
import FieldInput from '@magoosh/lib/formik_inputs/field_input';
import { unionBy } from 'lodash';
import { FontAwesomeIcon } from '@magoosh/lib/icons';
import fetch from 'utilities/fetch';
import { paths } from 'config/path_helpers';
import { bugsnagNotify } from 'utilities/bugsnag';

export const RecommendationEditor: React.FC<RecommendationEditorProps> = (props) => {
  const [modules, setModules] = useState(
    props.modules.map((module) => {
      return {
        ...module,
        lessons: module.lessons.map((lesson) => {
          return {
            ...lesson,
            hidden: true
          };
        })
      };
    })
  );

  const completedModules = modules.filter((module) => module.percentComplete === 100);

  const initialValues: RecommendationEditorFormValues = {
    recommendedModules: props.initialRecommendations || [],
    tutorFeedback: props.initialTutorFeedback || ''
  };

  const selectModule = (id): void => {
    const updatedModules = modules.map((module) => {
      return {
        ...module,
        selected: module.id === id ? !module.selected : module.selected
      };
    });
    setModules(updatedModules);
  };

  const clearSelectedModules = (): void => {
    const updatedModules = modules.map((module) => {
      return {
        ...module,
        selected: false
      };
    });
    setModules(updatedModules);
  };

  const addRecommendations = (recommendedModules, setFieldValue): void => {
    const selectedModules = modules.filter((module) => module.selected);
    setFieldValue('recommendedModules', unionBy(recommendedModules, selectedModules, 'id'));
    clearSelectedModules();
  };

  // filterModules function filters modules and study items based on the user input.
  // To acomplish this we first find out if the module is going to be displayed, if
  // this happen, we hide all its study items.
  // If module isn't going to be displayed we filter the study items to check if the input
  // matches the study items values.
  // The module is going to be displayed if its value matches the input value, or if any
  // study items value matches the input value
  const filterModules = (e): void => {
    const value = e.target.value.toLocaleLowerCase();
    const filteredModules = modules.map((module) => {
      const hideModule = !module.name.toLocaleLowerCase().includes(value);

      return {
        ...module,
        lessons: !hideModule
          ? module.lessons.map((lesson) => {
              return {
                ...lesson,
                hidden: true
              };
            })
          : module.lessons.map((lesson) => {
              return {
                ...lesson,
                hidden: value === '' || !lesson.name.toLocaleLowerCase().includes(value)
              };
            }),
        hidden:
          hideModule && !module.lessons.some((lesson) => lesson.name.toLocaleLowerCase().includes(value))
      };
    });
    setModules(filteredModules);
  };

  const displayCompleteness = (percentage) => {
    let completedText;
    switch (percentage) {
      case 0:
        completedText = 'Not started';
        break;
      case 100:
        completedText = 'Completed';
        break;
      default:
        completedText = `In progress (${percentage}%)`;
        break;
    }
    return completedText;
  };

  const handleOnSubmit = (
    values: RecommendationEditorFormValues,
    { setFieldError, setSubmitting }: FormikActions<RecommendationEditorFormValues>
  ): void => {
    setSubmitting(true);
    fetch(paths.api.mockTestRecommendationRequest(props.mockTestId), {
      method: 'PATCH',
      body: JSON.stringify({
        recommendationRequest: {
          recommendations: {
            freeform: values.tutorFeedback,
            modules: values.recommendedModules.map((module) => {
              return { id: module.id };
            })
          },
          state: 'complete',
          tutor_id: props.tutorId
        }
      })
    })
      .catch((error) => {
        const fieldErrors = error.errors;
        setFieldError('general', fieldErrors._error);
        bugsnagNotify(error);
      })
      .then(() => {
        location.reload();
      });
  };

  return (
    <Formik initialValues={initialValues} onSubmit={handleOnSubmit}>
      {({ errors, values, isSubmitting, submitForm, setFieldValue }) => (
        <Form>
          {(errors as any).general && <Alert bsStyle="danger">{(errors as any).general}</Alert>}
          <Row>
            <Col xs={6}>
              <FieldInput name="filter" type="input" label="Module or Lesson Name" onKeyUp={filterModules} />
              <div className={style.moduleSelector}>
                {modules.map((module) => (
                  <div
                    key={module.id}
                    className={cx(
                      style.module,
                      module.hidden && style.hidden,
                      module.selected && style.selected
                    )}
                    onClick={() => selectModule(module.id)}>
                    <p>
                      {module.name} ({module.difficulty}){' '}
                      <span>{displayCompleteness(module.percentComplete)}</span>
                    </p>

                    {module.lessons.some((lesson) => !lesson.hidden) && (
                      <small className={style.studyItem}>
                        - Includes the lesson: &quot;
                        {module.lessons.find((lesson) => !lesson.hidden).name}
                        &quot;
                      </small>
                    )}
                  </div>
                ))}
              </div>
              <SecondaryButton onClick={() => addRecommendations(values.recommendedModules, setFieldValue)}>
                Add Recommendation
              </SecondaryButton>
            </Col>
            <Col xs={6}>
              <div>
                <ControlLabel>Your Recommended Modules</ControlLabel>
              </div>

              <FieldArray
                name="recommendedModules"
                render={(arrayHelpers) =>
                  values.recommendedModules.map((module, index) => (
                    <Row key={index}>
                      <Col xs={10} md={6}>
                        <p key={module.id}>{module.name}</p>
                      </Col>
                      <Col xs={1} md={1} className={style.removeButton}>
                        <button onClick={() => arrayHelpers.remove(index)} type="button">
                          <FontAwesomeIcon icon={'minus-circle'} key={index} />
                        </button>
                      </Col>
                    </Row>
                  ))
                }
              />

              <br />

              <div>
                <ControlLabel>Modules Completed by the Student</ControlLabel>
              </div>
              {completedModules.map((module) => (
                <p key={module.id}>{module.name}</p>
              ))}
            </Col>
          </Row>
          <br />
          <FieldInput
            className={style.freeformTextArea}
            name="tutorFeedback"
            type="textarea"
            label="Personalized Response"
          />
          <br />

          <div>
            <strong>NOTE: When saving the first time, an email will be sent to the student</strong>
          </div>
          <PrimaryButton submitting={isSubmitting} onClick={submitForm}>
            Save Recommendations
          </PrimaryButton>
        </Form>
      )}
    </Formik>
  );
};

export default RecommendationEditor;
